import type { ContactDirectoryRow } from "@workspace/api-client-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

// Phase 37-04: person-first Member directory row table. Deliberately a plain
// `.map()` render — NOT wrapped in useReactTable (repo rule: unstable
// data/getCoreRowModel() args drive an infinite render loop; see memory
// feedback_no_tanstack_table_without_features). Display-only cells use plain
// markup, no shadcn FormLabel/FormControl (they throw without a FormField
// ancestor; memory feedback_formlabel_needs_formfield).

interface MemberDirectoryTableProps {
  rows: ContactDirectoryRow[];
  isLoading?: boolean;
  onRowClick: (contactId: number) => void;
}

function formatLastActivity(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function MemberDirectoryTable({
  rows,
  isLoading = false,
  onRowClick,
}: MemberDirectoryTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Last activity</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(5)].map((__, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="h-24 text-center text-muted-foreground"
              >
                No members found.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => onRowClick(row.id)}
                data-testid={`member-directory-row-${row.id}`}
              >
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-2">
                    {row.fullName}
                    {row.hasActiveMembership && (
                      <Badge variant="default" className="text-[10px]">
                        Active member
                      </Badge>
                    )}
                  </span>
                </TableCell>
                <TableCell>{row.accountLegalName}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>
                  {row.planCount === 0 ? (
                    <span className="text-sm text-muted-foreground">
                      — No active plan
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {row.planChips.map((chip, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">
                          {chip}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatLastActivity(row.lastActivityAt)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
